import { Params } from "@angular/router";
import { DealerInfo, IdName } from "@app-services/oem-dealer.service";
import { MakeModel, Model } from "@app-services/open.service";
import { PageControl } from "./../../../../shared/model/page-control";
import { Contact } from "@app/store/session/session.store";
import { PlateInfoProviderConfig } from "@app-model/oem-dealer";
import { FipeComparisonMode } from "@app-utils/fipe-comparison.utils";
import { parseDocumentFromQueryParam } from "@app-utils/document-utils";

export class AdvertisersRequestList {
  actualPage?: number;
  data?: OemDealerListItem[];
  numberOfPages?: number;
  totalElements?: number;
}
export class OemDealerListItem {
  auctions?: number;
  bought?: number;
  cities?: CityHelper[];
  cnpj?: number | string;
  createdAt?: string;
  evaluations?: number;
  hasLatePayment?: boolean;
  id?: string;
  imgUrl?: string;
  inactive?: boolean;
  lastAccess?: Date;
  latePayments?: number;
  name?: string;
}
export class CityHelper {
  id?: string;
  name?: string;
  uf?: string;
}

export type PublicationDefaultsBidOption =
  | 'DEFAULT'
  | 'ONLY_BID'
  | 'ONLY_FINAL_BID'
  | 'NONE';

export class PublicationDefaults {
  bidOption?: PublicationDefaultsBidOption | null;
  ignorePersonalizedOffers?: boolean | null;
  conditionalOffer?: boolean | null;
  allowLeadGeneration?: boolean | null;
  autoRenewal?: boolean | null;
}

// atualizar estes atributos
const OBJECT_KEY_NAMES = ["cityId", "searchQuery", "statuses", "types", "uf", "segments", "types"];
const CPF_KEY = ["cnpj"];
const NUMBER_KEY_NAMES = [];
const BOOLEAN_KEY_NAMES = ["signedContract"];
const IGNORED_KEY_NAMES = ["pageControl"];
const PAGE_CONTROL_KEY_NAMES = ["page", "sortField", "sortDirection"];

export class OemDealerFilter {
  auctionStatus?: string[];
  cityId?: string;
  pageControl?: PageControl;
  searchQuery?: string;
  statuses?: string[];
  uf?: string;
  cnpj?: string;
  segments?: string[];
  regionalId?: string;
  types?: string[];
  signedContract?: boolean;
  createdAtIni: string;
  createdAtEnd: string;

  toParamMap(): Params {
    const params: Params = {};
    Object.keys(this).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.stringify(this[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = this[k];
      }
      params[k] = value;
    });
    if (this.pageControl.page) {
      params.page = this.pageControl.page;
    }
    params.sortField = this.pageControl.sortField;
    params.sortDirection = this.pageControl.sortDirection;
    return params;
  }

  updateFromParamMap(params: Params): void {
    Object.keys(params).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(params[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = params[k];
      }

      if (NUMBER_KEY_NAMES.indexOf(k) >= 0) {
        value = Number(value);
        if (value === Number.NaN) {
          value = undefined;
        }
      } else if (BOOLEAN_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(value);
      } else if (CPF_KEY.indexOf(k) >= 0) {
        value = parseDocumentFromQueryParam(params[k]);
      }

      if (value !== undefined) {
        if (PAGE_CONTROL_KEY_NAMES.indexOf(k) >= 0) {
          // if (k === "page") {
          //   value--;
          // }
          this.pageControl[k] = value;
        } else {
          this[k] = value;
        }
      }
    });
  }
}

export class OemDealerDetails {
  cnpj?: string;
  comissionReport: OemDealerComissionDealer;
  dossierCost?: number;
  alwaysLoadFullDossier?: boolean;
  useEvalScoreABC?: boolean;
  evaluationCost?: number;
  evaluationStats?: OemDealerEvalVehicleStatsHelper;
  id?: string;
  imgUrl?: string;
  logoUrl: string;
  inactive?: boolean;
  locations?: OemDealerLocationHelper[];
  montlyEvaluationCost?: number;
  raioXPluginCost?: number;
  whatsAppNotificationCost?: number;
  ownerInfoCost?: number;
  ownerCheckEnabled?: boolean;
  enableChassisEval?: boolean;
  name?: string;
  legalName?: string;
  cityRegistration?: string;
  stateRegistration?: string;
  totvsCostCenter?: string | null;
  trialStatus?: string;
  signedContract?: boolean;

  createdAt?: Date;
  registeredBy?: string;
  type: string;
  target: 'ALL' | 'ONLY_COMPANIES' | 'ONLY_COMPANIES_WITH_CNAE';
  runNumberPlateIntegrationOnImport?: boolean;

  personalizations?: AllPersonalization[];
  pendingAuthorizationDealers?: DealerInfo[];

  phoneNumber: string;
  cityId: string;
  cityName: string;
  uf: string;
  email: string;
  authenticationMode?: string;
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  district: string;
  numEvaluationUnits?: string;
  numEvaluationUsers?: string;
  numSaleUsers?: string;
  numYards?: string;
  oemDealerStats?: OemDealerStats;
  regionalId?: string;
  regionalName?: string;
  balance?: OemDealerBalanceStats;
  evalPhotoTypes?: string[];
  sellingTerms?: string;
  expirationTime?: string;
  auctionDurationDays?: number;
  fastQuoteDuration?: string;
  fastQuoteCycles?: number;
  apikeys?: AARApiKey[];
  integrations?: OemDealerIntegration[];
  numPriorityDealersInFastQuotes?: number;
  segmentCashbackConfig: Map<string, ValueConfig>;
  segmentComissionConfig: Map<string, ValueConfig>;

  raioXPluginConfig?: RaioXPluginConfig;

  daysToExpireEval?: number;

  autoRenewal?: boolean;
  enableMolicarPricing?: boolean;
  enableOriginalEquipments?: boolean;
  enableRenavamAndOwner?: boolean;
  plateInfoProviderConfig?: PlateInfoProviderConfig | null;
  numberPlatePlus?: boolean;
  alwaysLoadFullDossierSell?: boolean;
  includeVehicleValueInvoice?: boolean;
  maxWhatsAppNotificationFastQuote?: number;
  openOnlineEvalEnabled?: boolean;
  sendEmailByEvalDealer: boolean;
  blockEvalReturnToDeskBuy: boolean;
  requiredExtraKeyAndCarManual: boolean;
  requiredPricingFields: boolean;
  disableSaleModuleEmails: boolean;
  showPriceOldEval: boolean;
  requiredReport: boolean;
  blockSellerToBuy: boolean;
  allowCreateAuctionWithSoldPlate: boolean;
  defaultExclusiveGroups: string[];
  requiredInfoOwner: boolean;

  dealersBlacklist?: string[];
  blockSellerSeeOffers: boolean;
  allowSellerToEvaluate: boolean;
  allowConditionalOffer: boolean;
  allowLeadGeneration: boolean;
  leadEmail: string;
  leadGenerationCollectCpfCnpj: boolean;
  includeExtraQuestionsInEval: boolean;
  fixedScore: number;
  redirectCarouselToFastQuote: boolean;
  fillPriceBeforePurchase: boolean;
  timeToAcceptFastQuoteOffer: string;
  allowPublicFastQuote: boolean;
  allowManualFinishTime: boolean;
  defaultCompetitiveFastQuote: boolean;
  publicationDefaults?: PublicationDefaults;
  autoPublishTradeIn: boolean;
  keepEvalStatusOnFastQuote: boolean;
  hideLocationOnFastQuote: boolean;
  requiredEvalOwnerConfirmation: boolean;
  blockViewEvalWithoutAssume: boolean;
  useAwaitingEntryStatus: boolean;
  autoCreateFastQuoteOnNegotiation: boolean;
  autoCreateFastQuoteOnPricing: boolean;
  useMinPriceOnAutoFastQuote: boolean;
  minimumProposalAcceptanceMargin?: number | null;
  showEvalDashboardCreated?: boolean;
  showEvalDashboardPerformed?: boolean;
  evalTypes?: string[];
  fipeComparisonMode?: FipeComparisonMode | null;
  platforms?: string[];
}

export class RaioXPluginConfig {
  defaultEvalDealerId: string;
  defaultEvalDealerName: string;
  enabled: boolean;
  expiresInDays: number;
  bonus: number;
  lastUpdatedBy: string;
  lastUpdatedAt: Date;
  defaultFipeRate: number;
  items: RaioXPluginConfigItem[];
  personalization: RaioXPluginPersonalization;
  finalOfferBonus: number;
  chooseEvalDealer: boolean;
}

export class RaioXPluginConfigItem {
  minYear: number;
  maxYear: number;
  minKm: number;
  maxKm: number;
  fipeRate: number;
  makeModelsMap: Map<string, string[]>;
  makes: IdName[];
  models: Model[];
  showingMakeOptions: boolean;
  showingModelOptions: boolean;
}

export class AllPersonalization {
  id: string;
  name: string;
  evalDealersIds: string[];
  templateDefault: boolean;
  alternativeDomain: string;
  creationStatus: string;
  currentStep: string;
}

export class RaioXPluginPersonalization {
  id: string;
  oemDealerId: string;
  oemDealerName: string;
  oemDealerLogo: string;
  name: string;
  headerFooterColor: string;
  templateDefault: boolean;
  creationStatus: string;
  currentStep: string;
  creationErrorMessage: string;
  additionalOemDealers: IdName[];
  additionalOemDealersIds: string[];
  evalDealers: IdName[];
  evalDealersIds: string[];
  primaryColor: string;
  buttonColor: string;
  buttonHoverColor: string;
  loginBgImageUrl: string;
  loginMobileBgImageUrl: string;
  onboardingAppImage1Url: string;
  onboardingAppImage2Url: string;
  logoUrl: string;
  raioXLogoUrl: string;
  raioXHeroImageUrl: string;
  raioXHomeTitle: string;
  raioXHomeSubtitle: string;
  raioXHomeTitleSubtitleColor: string;
  evaluationLogoUrl: string;
  evaluationHeroImageUrl: string;
  evaluationHomeTitle: string;
  evaluationHomeSubtitle: string;
  evaluationHomeTitleSubtitleColor: string;
  whatsAppNumber: string;
  whatsAppMessage: string;
  hideReasonField: boolean;
  mandatoryCpf: boolean;
  alternativeDomain: string;
  allowEvalScheduler: boolean;
  exitUrl: string;
  exitLabel: string;
  personalizedTermsCTA: string;
  personalizedTermsUrl: string;
  footerLinkText: string;
  footerLinkUrl: string;
  contactInfo: string;
  blockConfirmationEmail: boolean;
  colorContents: string;
  offerColor: string;
  sendEmailToSellers: boolean;
  menuItemColor: string;
  menuItemColorHover: string;
  favIcon: string;
  appIcon: string | null;
  noNotificationWhatsapp: boolean;
  allowedRegisterType: string[];
  emailsToReceiveLeads: string[];
  showSuperBidLogin: boolean;
  saleEmailText: string;
  showAllOffers: boolean;
  marketplaceMenuLabel: string;
  logoDestinationUrl: string;
  noAllowSensitiveData: boolean;
  exclusiveGroupsAllowed: string[]
  exclusiveGroups: IdName[]
  showOffersMarketplace: boolean;
  targetBuyers: string;
  invalidTargetBuyerErrorMessage: string;
  scripts: Script[];
  contacts: Contact[];
  footerLinks: FooterLink[];
  footerContacts: Contact[];
  allowPublicationScheduling: boolean;
  notShowAARRules: boolean;
  allowPublicAccess: boolean;
  transactional: boolean;
  defaultSegments: string[];
  sendEvalDealerNameInLead: boolean;
  allowADLogin: boolean;
  allowOnlyADLogin: boolean;
  federatedLoginConfigs: FederatedLoginConfig[];
  linkAboutUs: string;
  linkContact: string;
  noShowFooterLogo: boolean;
  noCustomizeEmails: boolean;
}

export class FooterLink {
  label: string;
  url: string;
}

export class Script {
  id: string;
  type: string;
  src: string;
}

export class FederatedLoginConfig {
  accessButtonLabelAD: string;
  appClientIdAD: string;
  identityProviderAD: string;
  cognitoDomainAD: string;
  optinTrackingAPIClass: string;
}

export class TargetBuyersLabel {
  static allTargets = ['ALL', 'ONLY_COMPANIES', 'ONLY_COMPANIES_WITH_CNAE', 'EXCLUSIVE_GROUPS'];

  static getLabel(type: string): string {
    switch (type) {
      case 'ALL': return 'Todos';
      case 'ONLY_COMPANIES': return 'Apenas PJ';
      case 'ONLY_COMPANIES_WITH_CNAE': return 'Apenas CNAE PERMITIDO';
      case 'EXCLUSIVE_GROUPS': return 'Grupos Exclusivos';
      default: return type;
    }
  }
}

export class ComissionCashbackValueConfigs {
  comissionConfig: ValueConfig;
  cashbackConfig: ValueConfig;
}

export class ValueConfig {
  type: 'DEFAULT' | 'REAL' | 'PERCENTAGE' | 'REAL_AND_PERCENTAGE';
  value: number;
  additionalFee: number;

  static getStaticFormatLabel(type: string): string {
    switch (type) {
      case 'REAL': return 'R$';
      case 'PERCENTAGE': return 'Porcentagem';
    }
  }

  getTypeLabel(): string {
    return this.type === 'DEFAULT' ? 'Padrão' : 'Personalizada';
  }

  getFormatLabel(): string {
    return ValueConfig.getStaticFormatLabel(this.type);
  }
}

export class OemDealerComissionDealer {
  items: PageHelperOemdealer;
  latePayments?: string;
  notPaid?: string;
  paid?: number;
}
export class OemDealerEvalVehicleStatsHelper {
  bought?: string;
  cancelled?: string;
  created?: string;
  evaluating?: string;
  expired?: string;
  fullDossier?: string;
  id?: string;
  negotiation?: string;
  princing?: string;
  returnedEval?: string;
  totalEvaluations?: number;
}
export class OemDealerLocationHelper {
  address?: string;
  addressNumber?: string;
  city?: CityHelper;
  name?: string;
  uf?: string;
  id?: string;
}
export class OemDealerStats {
  activeBids?: string;
  acceptedPersonalizedOffers?: string;
  bids?: string;
  bought?: string;
  cancelledBuy?: string;
  favorites?: string;
  id: StatId;
  impressions?: string;
  personalizedOffers?: string;
  rejectedPersonalizedOffers?: string;
  uniqueImpressions?: string;
  uniqueViews?: string;
  views?: number;
}
export class PageHelperOemdealer {
  actualPage?: string;
  data?: OemDealerComissionReportItem[];
  numberOfPages?: string;
  totalElements?: number;
}

export class StatId {
  date?: string;
  id?: string;
}
export class OemDealerComissionReportItem {
  bankslipUrl?: string;
  comissionValueCents?: string;
  dealerId?: string;
  dealerName?: string;
  expirationDate?: string;
  id?: string;
  latePayment?: boolean;
  latePaymentDate?: string;
  make?: string;
  manufactureYear?: string;
  model?: string;
  modelYear?: string;
  oemDealerId?: string;
  oemDealerName?: string;
  paid?: boolean;
  price?: string;
  soldAt?: string;
  trim?: string;
  vehicleId?: string;
  dealerCnpjCpf?: number;
  dealerEmail?: string;
  dealerPhoneNumbers?: string;
}

export class OemDealerForm {
  cnpj?: number | string;
  dossierCost?: number | null;
  evalPhotoTypes?: string[] = [];
  evaluationCost?: number | null;
  expirationTime?: string;
  auctionDurationDays?: number;
  fastQuoteDuration?: string;
  fastQuoteCycles?: number;
  id?: string;
  imgUrl?: string;
  inactive?: boolean;
  locations?: string[] = [];
  montlyEvaluationCost?: number | null;
  raioXPluginCost?: number | null;
  ownerInfoCost?: number | null;
  whatsAppNotificationCost?: number | null;
  ownerCheckEnabled?: boolean;
  enableChassisEval?: boolean;
  name?: string;
  legalName?: string;
  cityRegistration?: string;
  stateRegistration?: string;
  totvsCostCenter?: string | null;
  regionalId?: string;
  type?: string;
  sellingTerms?: string;

  trialStatus?: string | null;
  signedContract: boolean;

  alwaysLoadFullDossier?: boolean;
  useEvalScoreABC?: boolean;
  segmentCashbackConfig: any;
  segmentComissionConfig?: any;

  phoneNumber: string;
  cityId: string;
  cityName: string;
  uf: string;
  email: string;
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  district: string;
  target: string;
  defaultExclusiveGroups: string[];
  runNumberPlateIntegrationOnImport?: boolean;
  daysToExpireEval: number;
  autoRenewal?: boolean;
  enableMolicarPricing?: boolean;
  enableOriginalEquipments?: boolean;
  enableRenavamAndOwner?: boolean;
  plateInfoProviderConfig?: PlateInfoProviderConfig | null;
  numberPlatePlus?: boolean;
  alwaysLoadFullDossierSell?: boolean;
  includeVehicleValueInvoice?: boolean;
  maxWhatsAppNotificationFastQuote?: number;
  openOnlineEvalEnabled?: boolean;
  sendEmailByEvalDealer: boolean;
  blockEvalReturnToDeskBuy: boolean;
  requiredExtraKeyAndCarManual: boolean;
  requiredPricingFields: boolean;
  disableSaleModuleEmails: boolean;
  requiredReport: boolean;
  showPriceOldEval: boolean;
  blockSellerToBuy: boolean;
  allowCreateAuctionWithSoldPlate: boolean;
  requiredInfoOwner: boolean;
  allowSellerToEvaluate: boolean;
  blockSellerSeeOffers: boolean;
  allowConditionalOffer: boolean;
  allowLeadGeneration: boolean;
  leadEmail: string;
  leadGenerationCollectCpfCnpj: boolean;
  includeExtraQuestionsInEval: boolean;
  fixedScore: number;
  redirectCarouselToFastQuote: boolean;
  fillPriceBeforePurchase: boolean;
  timeToAcceptFastQuoteOffer: string;
  allowPublicFastQuote: boolean;
  allowManualFinishTime: boolean;
  defaultCompetitiveFastQuote: boolean;
  publicationDefaults?: PublicationDefaults;
  autoPublishTradeIn: boolean;
  keepEvalStatusOnFastQuote: boolean;
  hideLocationOnFastQuote: boolean;
  requiredEvalOwnerConfirmation: boolean;
  blockViewEvalWithoutAssume: boolean;
  useAwaitingEntryStatus: boolean;
  autoCreateFastQuoteOnNegotiation: boolean;
  autoCreateFastQuoteOnPricing: boolean;
  useMinPriceOnAutoFastQuote: boolean;
  minimumProposalAcceptanceMargin?: number | null;
  showEvalDashboardCreated?: boolean;
  showEvalDashboardPerformed?: boolean;
  evalTypes?: string[];
  fipeComparisonMode?: FipeComparisonMode | null;
  platforms?: string[];
}

export interface TotvsCostCenterConfig {
  defaultCostCenter: string;
}

export class OemDealerLocationForm {
  address?: string;
  addressNumber?: string;
  city?: CityHelper;
  id?: string;
  name?: string;
  zipCode?: string;
  cityId?: string;
  oemDealerId?: string;
  addressComplement?: string;
  regionalAarId?: string | null;
  makeId?: string;
  division?: string;
  sellingTerms?: string;
}

export class OemDealerBalanceStats {
  total: number;
  evaluations: number;
  evaluationsValue = 0;
  dossier: number;
  dossierValue = 0;
  cashback: number;
  cashbackValue = 0;
  monthlyPaymentEvaluation: number;
  monthlyPaymentEvaluationValue = 0;
  ownerInfo: number;
  ownerInfoValue = 0;
  whatsAppNotification: number;
  whatsAppNotificationValue = 0;
  raioXPlugins: number;
  raioXPluginsValue = 0;

  get totalDebit(): number {
    return this.evaluationsValue + this.dossierValue + this.monthlyPaymentEvaluationValue + this.ownerInfoValue + this.whatsAppNotificationValue + this.raioXPluginsValue;
  }

  get totalCredit(): number {
    return this.cashbackValue;
  }
}

export class EvalDealerFilter {
  oemDealerId?: string;
  pageControl?: PageControl;
  searchQuery?: string;
}

export class RequestUnits {
  actualPage?: number;
  data?: EvalDealerHelper[];
  numberOfPages?: number;
  totalElements?: number;
}
export class EvalDealerHelper {
  address?: string;
  addressComplement?: string;
  addressNumber?: string;
  cityId?: string;
  cityName?: string;
  cnpj?: number | string;
  make: MakeModel;
  district?: string;
  email?: string;
  evalUsers?: EvalUserHelper[];
  id?: string;
  inactive?: boolean;
  name?: string;
  oemDealerId?: string;
  phoneNumbers?: string[];
  uf?: string;
  zipCode?: string;
  virtualEvalDealer?: boolean;
  pluginRaioXContactInfo?: string;
  physicalEvalDealers: string[]
  personalizationId?: string;
  oemDealerLocationId?: string;
  division: string;
  externalId: string;
  regionalAarId?: string | null;
  regional?: { id: string; name: string } | null;

  // helper
  showAllUsers?: boolean;
}
export class EvalUserHelper {
  email?: string;
  id?: string;
  name?: string;
  roles?: string[];
}

export class EvalDealerForm {
  address?: string;
  addressComplement?: string;
  addressNumber?: string;
  cityId?: string;
  cnpj?: number | string;
  district?: string;
  email?: string;
  externalId: string;
  virtualEvalDealer?: boolean;
  id?: string;
  inactive?: boolean;
  makeId?: string;
  name?: string;
  oemDealerId?: string;
  phoneNumbers?: string[] = [];
  zipCode?: string;
  pluginRaioXContactInfo?: string;
  physicalEvalDealers: string[];
  oemDealerLocationId?: string;
  newOemDealerLocation: boolean;
  regionalAarId?: string | null;
}

export class AARApiKey {
  name: string;
  keyPrefix: string;
  createdAt: Date;
}

export class OemDealerIntegration {
  id: string;
  oemDealerId: string;
  provider: string;
  type: string;
  token: string;
  tokenExpiresAt: Date;
  login: string;
  hasPassword: boolean;
  password: string;
  url: string;
  createdAt: Date;
  syncing: boolean;
  lastUpdatedAt: Date;
  companyIds: any[]
  evalDealerIntegrations: EvalDealerIntegration[];
  dataSource: string;
  nbsConfiguration?: NbsConfiguration;
  superBidConfiguration?: SuperBidConfiguration;
  syonetConfiguration?: SyonetConfiguration;
  autoGestorConfiguration?: AutoGestorConfiguration;
}

export class AutoGestorConfiguration {
  imagesInPreparationIdentifiers?: string[];
  usePlateSearchForTrim?: boolean;
}

export class NbsConfiguration {
  notaModelo?: string;
  classeContabil?: string;
  dataSource?: string;
  skipEquipmentOnError?: boolean;
  useSellerCpfAsLoggedUser?: boolean;
  cpfDefault?: number | string;
}

export class SyonetConfiguration {
  companyId: number;
  eventGroup: string;
  eventType: string;
  source: string;
  media: string;
}

export class SuperBidConfiguration {
  eventId: number;
  publicEventGroups?: SuperBidGroupIds;
  customEventGroups: CustomEventGroup[];
  allowedTargets: string[];
  sendWinnerToSuperBid: boolean;

  allEventsGroups?: IdName[];
}

export class SuperBidGroupIds {
  publicGroupId: number;
  onlyPJGroupId: number;
  onlyCNAEGroupId: number;
}

export class CustomEventGroup {
  id: number;
  name: string;
  charges: any[];
}

export default class EvalDealerIntegration {
  integrationId: string;
  evalDealerId: string;
  evalDealerName?: string;
  login: string;
  password?: string;
  url?: string;
  dataSource: string;
  nbsConfiguration?: NbsConfiguration;
  token: string;
  disableToEvalDealer?: boolean;
}

export class IntegrationProviderLabel {
  static allProviders = ['LINX', 'LEADS_BRIDGE', 'NBS', 'REVENDA_MAIS', 'SUPERVISAO', 
    'UNION_SOLUTIONS', 'MR_SALES', 'SYONET_CRM', 'NBS_CARRERA', 'VENDA_ONLINE_CARRERA', 
    'TOYOTA', 'AD_SET', "DEALER_NET", 'ALTIMUS', 'SUPER_BID', 'PROCEMAX', 'BID_PARK', 'AUTO_GESTOR'];

  static getLabel(type: string): string {
    switch (type) {
      case 'SUPERVISAO': return 'SuperVisão';
      case 'PROCEMAX': return 'Procemax';
      case 'UNION_SOLUTIONS': return 'Union Solutions';
      case 'REVENDA_MAIS': return 'RevendaMais';
      case 'NBS': return 'NBS';
      case 'LINX': return 'Linx';
      case 'LEADS_BRIDGE': return 'Leads Bridge';
      case 'MR_SALES': return 'Mr. Sales';
      case 'SYONET_CRM': return 'Syonet CRM';
      case 'NBS_CARRERA': return 'NBS (Carrera)';
      case 'VENDA_ONLINE_CARRERA': return 'Venda Online (Carrera)';
      case 'TOYOTA': return 'Toyota';
      case 'AD_SET': return 'AdSet';
      case 'DEALER_NET': return 'DealerNet';
      case 'ALTIMUS': return 'Altimus'
      case 'SUPER_BID': return 'Super Bid'
      case 'BID_PARK': return 'Bid Park (sbX)'
      case 'AUTO_GESTOR': return 'Auto Gestor'
      default: return type;
    }
  }
}

export class IntegrationTypeLabel {
  static allTypes = ['EVAL_INPUT', 'EVAL_OUTPUT', 'VEHICLE_INPUT', 'INSPECTION_REPORT_INPUT', 'LEAD_OUTPUT', 'VEHICLE_OUTPUT'];

  static getLabel(type: string): string {
    switch (type) {
      case 'INSPECTION_REPORT_INPUT': return 'Laudos';
      case 'EVAL_INPUT': return 'Captações (input)';
      case 'EVAL_OUTPUT': return 'Captações (output)';
      case 'VEHICLE_INPUT': return 'Estoque (input)';
      case 'LEAD_OUTPUT': return 'Leads (output)';
      case 'VEHICLE_OUTPUT': return 'Ofertas (output)';
      case 'AUCTION_DEALER_OUTPUT': return 'Eventos (propostas, favoritos) - (output)';
      default: return type;
    }
  }
}
